import type { SymbolType } from "../../../types/floorplannerTypes";
import type { UserModel3DLibraryItem } from "./userModel3DTypes";

export type UserModel3DCommerceStamp = {
  price?: number;
  currency?: string;
  /** Product / store URL (library `url`); distinct from GLB `userModel.url`. */
  urlStore?: string;
  brand?: string;
};

/**
 * Commerce fields to stamp onto `threeD.userModel` at place / after library edit.
 * Omits empty values so Product List can treat missing fields as unavailable.
 */
export const commerceStampFromLibraryItem = (
  model: Pick<
    UserModel3DLibraryItem,
    "price" | "currency" | "url" | "brand"
  >,
): UserModel3DCommerceStamp => {
  const stamp: UserModel3DCommerceStamp = {};
  if (typeof model.price === "number" && Number.isFinite(model.price)) {
    stamp.price = model.price;
    stamp.currency = model.currency?.trim() || "SEK";
  }
  const storeUrl = model.url?.trim();
  if (storeUrl) stamp.urlStore = storeUrl;
  const brand = model.brand?.trim();
  if (brand) stamp.brand = brand;
  return stamp;
};

/**
 * Refresh stamped commerce on every placed symbol for this library model id.
 * Returns how many symbols were updated.
 */
export const syncUserModel3DCommerceOnPlacements = (
  symbolsMap: { values: () => IterableIterator<SymbolType> },
  modelId: string,
  commerce: UserModel3DCommerceStamp,
  updateThreeD: (symbolId: string, threeD: SymbolType["threeD"]) => void,
): number => {
  let updated = 0;
  for (const symbol of symbolsMap.values()) {
    const userModel = symbol.threeD?.userModel;
    if (!userModel || userModel.modelId !== modelId) continue;
    const nextThreeD = {
      ...symbol.threeD,
      userModel: {
        ...userModel,
        price: commerce.price,
        currency: commerce.currency,
        urlStore: commerce.urlStore,
        brand: commerce.brand,
      },
    };
    // Clear omitted fields so stale price/url/brand do not linger after edit.
    if (commerce.price === undefined) {
      delete nextThreeD.userModel.price;
      delete nextThreeD.userModel.currency;
    }
    if (commerce.urlStore === undefined) {
      delete nextThreeD.userModel.urlStore;
    }
    if (commerce.brand === undefined) {
      delete nextThreeD.userModel.brand;
    }
    updateThreeD(symbol.id, nextThreeD);
    updated += 1;
  }
  return updated;
};
